import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import {
  Upload, Wand2, Download, RefreshCw, Check, ImagePlus, Loader2, Package, Palette, Lock, Sparkles, Maximize2, X,
} from "lucide-react";
import { Header } from "@/components/Header";
import { api, mediaUrl, fileToResizedBase64, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Presentation() {
  const { user, company, refresh } = useAuth();
  const navigate = useNavigate();
  const [kitchen, setKitchen] = useState(null);
  const [tree, setTree] = useState([]);
  const [sel, setSel] = useState({}); // { [featureId]: { option, color } }
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState("");
  const [refining, setRefining] = useState(false);
  const [viewer, setViewer] = useState(false);
  const fileRef = useRef();

  const active = company?.subscription_active;
  const cpg = company?.credits_per_generation || 10;
  const balance = company?.credit_balance ?? 0;
  const gensLeft = Math.floor(balance / cpg);
  const trialLeft = gensLeft;
  const trialBlocked = balance < cpg;
  const lowCredits = active && gensLeft <= 5;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/library/tree");
        setTree(data.products || []);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error(e);
        toast.error("Couldn't load your library. Please refresh.");
      }
    })();
  }, []);

  const pickOption = (feature, option) => setSel((s) => {
    const cur = s[feature.id];
    if (cur?.option?.id === option.id) {
      const next = { ...s }; delete next[feature.id]; return next;
    }
    const avail = (feature.colors || []).filter((c) => (option.available_color_ids || []).includes(c.id));
    return { ...s, [feature.id]: { option, color: avail.length === 1 ? avail[0] : null } };
  });
  const pickColor = (featureId, color) => setSel((s) => ({
    ...s, [featureId]: { ...s[featureId], color: s[featureId]?.color?.id === color.id ? null : color },
  }));

  const changes = useMemo(() => {
    const out = [];
    tree.forEach((p) => (p.features || []).forEach((f) => {
      const s = sel[f.id];
      if (!s?.option) return;
      out.push({
        product: p.name,
        feature: f.name,
        label: f.name,
        name: s.option.name || null,
        color: s.color?.name || null,
        hex: s.color?.hex || null,
        storage_path: s.option.storage_path || null,
        color_storage_path: s.color?.storage_path || null,
      });
    }));
    return out;
  }, [tree, sel]);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const { base64, dataUrl } = await fileToResizedBase64(file);
      setKitchen({ base64, dataUrl });
      setResult(null);
    } catch (e) {
      toast.error("Could not read that image.");
    }
  };

  const handleErr = (e) => {
    const status = e?.response?.status;
    if (status === 402) {
      toast.error(apiError(e.response?.data?.detail), {
        action: company?.subscription_active
          ? { label: "Buy credits", onClick: () => navigate("/store") }
          : { label: "Subscribe", onClick: () => navigate("/team") },
      });
      return true;
    }
    if (status === 429) {
      toast.error(apiError(e.response?.data?.detail));
      return true;
    }
    return false;
  };

  const generate = async () => {
    if (!kitchen) return toast.error("Upload a photo first.");
    if (changes.length === 0 && !prompt.trim()) return toast.error("Pick something to change or add instructions.");
    if (trialBlocked) {
      return toast.error(active ? "Out of credits. Buy a credit pack to keep generating." : "Your free trial has ended. Subscribe to keep generating.", {
        action: { label: active ? "Buy credits" : "Subscribe", onClick: () => navigate("/store") },
      });
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/generate", { kitchen_image: kitchen.base64, changes, prompt });
      setResult(data.image);
      setViewer(true);
      await refresh();
      toast.success("Render ready!");
    } catch (e) {
      if (!handleErr(e)) toast.error(apiError(e?.response?.data?.detail) || "Generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const refine = async () => {
    if (!result || !refinePrompt.trim()) return;
    setRefining(true);
    try {
      const { data } = await api.post("/refine", { current_image: result, prompt: refinePrompt });
      setResult(data.image);
      setRefinePrompt("");
      await refresh();
      toast.success("Adjusted!");
    } catch (e) {
      if (!handleErr(e)) toast.error(apiError(e?.response?.data?.detail) || "Adjustment failed.");
    } finally {
      setRefining(false);
    }
  };

  const download = async () => {
    const dataUrl = `data:image/png;base64,${result}`;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "customize-studio-render.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Customize Studio render" });
        return;
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // user dismissed the share/save sheet
    }
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "customize-studio-render.png";
    a.click();
  };

  const noCats = tree.length === 0;

  return (
    <div className="min-h-screen lg:flex lg:h-[100dvh] lg:min-h-0 lg:flex-col lg:overflow-hidden lg:overscroll-none">
      <Header />
      <div className="grid grid-cols-1 lg:min-h-0 lg:flex-1 lg:grid-cols-[400px_1fr] lg:overflow-hidden">
        {/* Configurator */}
        <aside className="thin-scroll relative border-r border-[var(--line)] bg-white lg:h-full lg:overflow-y-auto lg:overscroll-contain">
          <div className="space-y-6 p-6 pb-8 md:p-7">
            {!active && (
              <div data-testid="trial-banner" className="rounded-xl border border-[var(--brand)]/40 bg-[var(--brand)]/[0.06] p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold">
                    {trialBlocked ? "Free trial ended" : `Free trial · ${gensLeft} free generation${gensLeft === 1 ? "" : "s"} left`}
                  </p>
                  <Sparkles className="h-4 w-4 text-[var(--brand)]" />
                </div>
                <p className="mt-1 text-xs text-[var(--ink-soft)]">
                  {trialBlocked ? "Subscribe to get 1,000 credits and unlock your whole team." : "Build your library free — subscribe anytime to keep generating."}
                </p>
                <button data-testid="trial-subscribe-btn" onClick={() => navigate("/store")}
                  className="mt-3 w-full rounded-full bg-[var(--brand)] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)]">
                  Subscribe
                </button>
              </div>
            )}

            {active && null}

            <div>
              <p className="overline">Step 01</p>
              <h2 className="mt-1 font-display text-xl tracking-tight">Upload the photo</h2>
            </div>

            <button data-testid="upload-kitchen-btn" onClick={() => fileRef.current?.click()}
              className="group relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-[var(--line)] bg-[var(--bone)] transition-colors duration-200 hover:border-[var(--brand)]">
              {kitchen ? (
                <img src={kitchen.dataUrl} alt="photo" className="h-full w-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-[var(--ink-soft)]">
                  <Upload className="h-7 w-7" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Click to upload a photo</span>
                  <span className="text-xs">JPG or PNG</span>
                </div>
              )}
              {kitchen && <span className="absolute bottom-2 right-2 rounded-full bg-[var(--ink)] px-3 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">Replace</span>}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="kitchen-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />

            {/* What are we changing? — Product > Feature > Option > Color */}
            <div>
              <p className="overline">Step 02</p>
              <h2 className="mt-1 font-display text-xl tracking-tight">What are we changing?</h2>

              {noCats ? (
                <div data-testid="studio-no-cats" className="mt-4 rounded-lg border border-dashed border-[var(--line)] bg-[var(--bone)] p-5 text-sm text-[var(--ink-soft)]">
                  Your library is empty. {["owner", "manager"].includes(user.role)
                    ? <>Head to the <button onClick={() => navigate("/library")} className="font-semibold text-[var(--brand)]">Library</button> to add your services, features, options and colors.</>
                    : "Ask your owner or manager to set up the product Library."}
                </div>
              ) : (
                <div className="mt-4 space-y-6">
                  {tree.map((p) => (
                    <div key={p.id} data-testid={`studio-product-${p.id}`}>
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-[var(--brand)]" strokeWidth={1.75} />
                        <h3 className="font-display text-lg tracking-tight">{p.name}</h3>
                      </div>
                      {(p.features || []).length === 0 && <p className="mt-1 text-xs text-[var(--ink-soft)]">No features yet.</p>}
                      <div className="mt-3 space-y-4">
                        {(p.features || []).map((f) => (
                          <FeatureBlock key={f.id} feature={f} selection={sel[f.id]} onPickOption={pickOption} onPickColor={pickColor} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Prompt */}
            <div>
              <p className="overline">Step 03</p>
              <h2 className="mt-1 font-display text-xl tracking-tight">Guide the AI</h2>
              <textarea data-testid="prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                placeholder="e.g. warm afternoon light, keep the landscaping, matte finish…"
                className="mt-3 w-full resize-none rounded-lg border border-[var(--line)] bg-[var(--bone)] p-3 text-sm outline-none transition-colors duration-200 focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20" />
            </div>
          </div>
        </aside>

        {/* Stage */}
        <main className="relative flex flex-col bg-[var(--bone)] p-6 md:p-8 lg:h-full lg:min-h-0 lg:overflow-hidden">
          <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3">
            <div>
              <p className="overline">Presentation</p>
              <h1 className="font-display text-xl tracking-tight md:text-2xl">The Vision</h1>
            </div>
            {result && (
              <div className="flex items-center gap-2">
                <button data-testid="fullscreen-btn" onClick={() => setViewer(true)} title="Open full screen"
                  className="flex items-center gap-2 rounded-full border border-[var(--line)] bg-white px-4 py-2.5 text-sm font-medium transition-transform duration-200 ease-out hover:-translate-y-px">
                  <Maximize2 className="h-4 w-4" strokeWidth={1.75} /><span className="hidden sm:inline">Full screen</span>
                </button>
                <button data-testid="download-btn" onClick={download} title="Save image to gallery"
                  className="flex items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2.5 text-sm font-medium text-white transition-transform duration-200 ease-out hover:-translate-y-px">
                  <Download className="h-4 w-4" strokeWidth={1.75} /><span className="hidden sm:inline">Save image</span>
                </button>
              </div>
            )}
          </div>

          <div className="mx-auto mt-3 w-full max-w-5xl">
            {loading ? (
              <div className="flex aspect-[16/10] max-h-[50dvh] w-full animate-pulse flex-col items-center justify-center gap-4 rounded-xl border border-[var(--line)] bg-white text-[var(--ink-soft)]">
                <Loader2 className="h-8 w-8 animate-spin text-[var(--brand)]" />
                <p className="text-sm font-medium">Designing the new look…</p>
              </div>
            ) : result ? (
              <div className="flex w-full flex-col overflow-hidden rounded-xl border-2 border-[var(--line)] bg-white shadow-md blur-in">
                <div className="flex items-center justify-between border-b border-[var(--line)] bg-[var(--surface-2)] px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-soft)]">
                  <span>Before</span>
                  <span className="text-[10px] normal-case">Drag to compare</span>
                  <span>After</span>
                </div>
                <ReactCompareSlider data-testid="before-after-slider" onlyHandleDraggable={false}
                  style={{ touchAction: "none", maxHeight: "50dvh" }}
                  itemOne={<ReactCompareSliderImage src={kitchen.dataUrl} alt="Before" style={{ objectFit: "contain" }} />}
                  itemTwo={<ReactCompareSliderImage src={`data:image/png;base64,${result}`} alt="After" style={{ objectFit: "contain" }} />} />
              </div>
            ) : kitchen ? (
              <div className="w-full overflow-hidden rounded-xl border border-[var(--line)] bg-white shadow-sm">
                <img src={kitchen.dataUrl} alt="photo" className="mx-auto max-h-[50dvh] w-full object-contain" />
              </div>
            ) : (
              <div className="flex aspect-[16/10] max-h-[50dvh] w-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[var(--line)] text-[var(--ink-soft)]">
                <ImagePlus className="h-10 w-10" strokeWidth={1.25} />
                <p className="text-sm">Upload a photo to begin</p>
              </div>
            )}
          </div>

          {/* Generate — locked directly under the photo */}
          <div className="mx-auto mt-4 w-full max-w-5xl">
            <button data-testid="generate-render-button" onClick={generate} disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--brand)] py-3.5 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)] disabled:opacity-60">
              {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Rendering…</>)
                : trialBlocked ? (<><Lock className="h-4 w-4" /> {active ? "Buy credits to generate" : "Subscribe to generate"}</>)
                : (<><Wand2 className="h-4 w-4" strokeWidth={2} /> Generate Render</>)}
            </button>
          </div>

          {result && (
            <div className="mx-auto mt-3 flex w-full max-w-5xl flex-col gap-3 rounded-xl border border-[var(--line)] bg-white p-4 sm:flex-row sm:items-center">
              <RefreshCw className="hidden h-5 w-5 shrink-0 text-[var(--brand)] sm:block" strokeWidth={1.5} />
              <input data-testid="refine-input" value={refinePrompt} onChange={(e) => setRefinePrompt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && refine()}
                placeholder="Adjust it — e.g. make it a touch darker, add more contrast…"
                className="flex-1 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20" />
              <button data-testid="refine-btn" onClick={refine} disabled={refining || !refinePrompt.trim()}
                className="flex items-center justify-center gap-2 rounded-full bg-[var(--ink)] px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px disabled:opacity-50">
                {refining ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Adjust
              </button>
            </div>
          )}
        </main>
      </div>

      {/* Full-screen result viewer */}
      {viewer && result && (
        <div data-testid="result-viewer" className="fixed inset-0 z-[70] flex flex-col bg-black/95">
          <button data-testid="viewer-close-btn" onClick={() => setViewer(false)} title="Exit"
            className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25">
            <X className="h-6 w-6" strokeWidth={2} />
          </button>
          <div className="flex min-h-0 flex-1 items-center justify-center p-3 md:p-8">
            <ReactCompareSlider data-testid="viewer-slider" onlyHandleDraggable={false}
              style={{ touchAction: "none", width: "100%", height: "100%" }}
              itemOne={<ReactCompareSliderImage src={kitchen.dataUrl} alt="Before" style={{ objectFit: "contain" }} />}
              itemTwo={<ReactCompareSliderImage src={`data:image/png;base64,${result}`} alt="After" style={{ objectFit: "contain" }} />} />
          </div>
          <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="w-full max-w-xl rounded-xl bg-white/95 p-3 shadow-lg backdrop-blur">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-[var(--ink)]"><Wand2 className="h-3.5 w-3.5 text-[var(--brand)]" /> Make changes</p>
              <div className="flex gap-2">
                <input data-testid="viewer-refine-input" value={refinePrompt} onChange={(e) => setRefinePrompt(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && refine()}
                  placeholder="Type a tweak — or exit to pick from your swatches…"
                  className="flex-1 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20" />
                <button data-testid="viewer-refine-btn" onClick={refine} disabled={refining || !refinePrompt.trim()}
                  className="flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-px disabled:opacity-50">
                  {refining ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Adjust
                </button>
              </div>
              <button data-testid="viewer-edit-swatches-btn" onClick={() => setViewer(false)}
                className="mt-2 text-xs font-semibold text-[var(--brand)] underline">Exit to pick from swatches</button>
            </div>
            <button data-testid="viewer-save-btn" onClick={download}
              className="flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[var(--ink)] shadow-lg transition-transform hover:-translate-y-px">
              <Download className="h-4 w-4" strokeWidth={1.75} /> Save image
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FeatureBlock({ feature, selection, onPickOption, onPickColor }) {
  const options = feature.options || [];
  const selOpt = selection?.option;
  const availColors = selOpt
    ? (feature.colors || []).filter((c) => (selOpt.available_color_ids || []).includes(c.id))
    : [];
  return (
    <div data-testid={`studio-feature-${feature.id}`} className="rounded-lg border border-[var(--line)] bg-white p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">{feature.name}</h4>
        {selOpt && <span className="truncate text-xs text-[var(--ink-soft)]">{selOpt.name}{selection?.color ? ` · ${selection.color.name}` : ""}</span>}
      </div>
      {options.length === 0 ? (
        <p className="text-xs text-[var(--ink-soft)]">No options yet. Add them in the Library.</p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {options.map((o) => {
            const on = selOpt?.id === o.id;
            return (
              <button key={o.id} data-testid={`studio-option-${o.id}`} onClick={() => onPickOption(feature, o)} title={o.name}
                className={`group relative aspect-square overflow-hidden rounded-lg border transition-transform duration-200 ease-out hover:-translate-y-px ${on ? "border-[var(--brand)] ring-2 ring-[var(--brand)] ring-offset-2" : "border-[var(--line)]"}`}>
                {o.image_url ? <img src={mediaUrl(o.image_url)} alt={o.name} className="h-full w-full object-contain p-2" />
                  : <div className="flex h-full w-full items-center justify-center bg-[var(--surface-2)] text-[10px] text-[var(--ink-soft)]">{o.name}</div>}
                <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-1 text-[10px] font-medium text-white">{o.name}</span>
                {on && <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand)] text-white"><Check className="h-3 w-3" strokeWidth={3} /></span>}
              </button>
            );
          })}
        </div>
      )}
      {selOpt && (
        <div className="mt-3 border-t border-[var(--line)] pt-3">
          <div className="mb-2 flex items-center gap-1.5"><Palette className="h-3.5 w-3.5 text-[var(--ink-soft)]" /><span className="text-xs font-semibold">Color / finish</span></div>
          {availColors.length === 0 ? (
            <p className="text-xs text-[var(--ink-soft)]">No colors ticked for {selOpt.name}. Enable some in the Library.</p>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              {availColors.map((c) => {
                const on = selection?.color?.id === c.id;
                return (
                  <button key={c.id} data-testid={`studio-color-${feature.id}-${c.id}`} onClick={() => onPickColor(feature.id, c)} title={c.name}
                    className={`relative h-9 w-9 overflow-hidden rounded-full transition-transform duration-200 ease-out hover:-translate-y-px ${on ? "ring-2 ring-[var(--brand)] ring-offset-2" : "ring-1 ring-[var(--line)]"}`}
                    style={{ backgroundColor: c.hex || "#ccc" }}>
                    {c.image_url && <img src={mediaUrl(c.image_url)} alt={c.name} className="h-full w-full object-cover" />}
                    {on && <Check className="absolute inset-0 m-auto h-4 w-4 text-white mix-blend-difference" strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

